import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Download,
  Copy,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  Building,
  Hash,
  Layers,
  Cpu,
  DollarSign,
  Eye,
  EyeOff,
  FileJson,
  Image,
  Maximize2,
  Minimize2,
  File,
  ChevronDown,
  ChevronRight,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { sipotmGetWorkOrder, sipotmGetWorkOrderFileUrl, sipotmGetWorkOrderPreviewUrl } from "../api";
import "./SIPOTMWorkOrderDetail.css";

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="sipotm-collapsible-section">
      <button className="sipotm-section-header" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <span>{title.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
      </button>
      {isOpen && <div className="sipotm-section-content">{children}</div>}
    </div>
  );
};

// Recursive JSON Renderer for Raw JSON view
const JsonRenderer = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) {
    return <span className="sipotm-json-null">null</span>;
  }

  if (typeof data === "boolean") {
    return <span className="sipotm-json-boolean">{data.toString()}</span>;
  }

  if (typeof data === "number") {
    return <span className="sipotm-json-number">{data}</span>;
  }

  if (typeof data === "string") {
    return <span className="sipotm-json-string">"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="sipotm-json-empty">[]</span>;
    return (
      <div className="sipotm-json-array">
        {data.map((item, index) => (
          <div key={index} className="sipotm-json-array-item" style={{ marginLeft: depth * 16 }}>
            <span className="sipotm-json-index">[{index}]</span>
            <JsonRenderer data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="sipotm-json-empty">{"{}"}</span>;
    return (
      <div className="sipotm-json-object">
        {entries.map(([key, value]) => (
          <div key={key} className="sipotm-json-property" style={{ marginLeft: depth * 16 }}>
            <span className="sipotm-json-key">{key}:</span>
            <JsonRenderer data={value} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(data)}</span>;
};

const SIPOTMWorkOrderDetail = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();
  
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("sip");
  const [copiedField, setCopiedField] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(true);
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(50); // Default to 50% for images
  
  // File/Preview state
  const [fileLoadError, setFileLoadError] = useState(false);

  useEffect(() => {
    fetchWorkOrder();
  }, [workOrderId]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await sipotmGetWorkOrder(workOrderId);
      setWorkOrder(data);
    } catch (err) {
      console.error("Failed to fetch work order:", err);
      setError(err.message || "Failed to load work order");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const url = sipotmGetWorkOrderFileUrl(workOrderId);
    window.open(url, "_blank");
  };

  const handleDownloadJson = () => {
    if (!workOrder) return;
    const data = {
      sip_data: workOrder.sip_data,
      otm_data: workOrder.otm_data,
      field_coordinates: workOrder.field_coordinates
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^.]+$/, "") || workOrderId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = useCallback((text, fieldName) => {
    navigator.clipboard.writeText(typeof text === 'object' ? JSON.stringify(text, null, 2) : String(text)).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle size={20} className="status-icon completed" />;
      case "failed": return <AlertCircle size={20} className="status-icon failed" />;
      case "processing":
      case "extracting": return <RefreshCw size={20} className="status-icon processing spin" />;
      default: return <Clock size={20} className="status-icon pending" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "completed": return "status-completed";
      case "failed": return "status-failed";
      case "processing":
      case "extracting": return "status-processing";
      default: return "status-pending";
    }
  };

  const getFieldCount = (data) => {
    if (!data) return 0;
    return Object.values(data).filter(v => v && v !== "").length;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const isImageFile = (filename) => {
    const ext = getFileExtension(filename);
    // TIFF files are converted to PDF by the preview endpoint, so don't treat them as images
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
  };

  // Check if file is TIFF (will be converted to PDF for preview)
  const isTiffFile = (filename) => {
    const ext = getFileExtension(filename);
    return ['tiff', 'tif'].includes(ext);
  };

  const isPdf = workOrder?.filename?.toLowerCase().endsWith('.pdf');
  const previewUrl = workOrderId ? sipotmGetWorkOrderPreviewUrl(workOrderId) : null;

  if (loading) {
    return (
      <div className="sipotm-detail-loading">
        <RefreshCw size={32} className="spin" />
        <p>Loading work order...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sipotm-detail-error">
        <AlertCircle size={48} />
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate("/sip-otm/history")}>
          <ArrowLeft size={18} /> Back to Work Orders
        </button>
      </div>
    );
  }

  if (!workOrder) return null;

  return (
    <div className="sipotm-work-order-detail">
      {/* Back Button */}
      <button className="sipotm-back-btn" onClick={() => navigate("/sip-otm/history")}>
        <ArrowLeft size={18} /> Back to Work Orders
      </button>

      {/* Header */}
      <div className="sipotm-detail-header">
        <div className="sipotm-detail-header-left">
          <div className="sipotm-detail-title">
            <h1>
              <FileText size={24} />
              {workOrder.filename || "Work Order"}
            </h1>
            <span className="sipotm-form-badge">
              {workOrder.form_type?.toUpperCase() || 'SIP_OTM'}
            </span>
          </div>
          <div className="sipotm-detail-meta">
            <span className="sipotm-meta-item">
              <Hash size={14} /> <strong>ID:</strong> {workOrder.work_order_id || workOrder._id}
              <button
                className="sipotm-copy-btn-inline"
                onClick={() => copyToClipboard(workOrder.work_order_id || workOrder._id, "id")}
                title="Copy ID"
              >
                <Copy size={14} />
                {copiedField === "id" && <span className="copied-tooltip">Copied!</span>}
              </button>
            </span>
            {workOrder.uid && (
              <span className="sipotm-meta-item">
                <Hash size={14} /> <strong>UID:</strong> {workOrder.uid}
              </span>
            )}
          </div>
        </div>

        <div className="sipotm-detail-header-right">
          <div className={`sipotm-status-badge ${getStatusClass(workOrder.status)}`}>
            {getStatusIcon(workOrder.status)}
            {workOrder.status}
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="sipotm-info-cards">
        <div className="sipotm-info-card">
          <Hash size={20} />
          <div>
            <span className="label">Work Order ID</span>
            <span className="value">{workOrder.work_order_id || workOrder._id}</span>
          </div>
        </div>
        <div className="sipotm-info-card">
          <FileText size={20} />
          <div>
            <span className="label">Filename</span>
            <span className="value">{workOrder.filename || '-'}</span>
          </div>
        </div>
        <div className="sipotm-info-card">
          <Layers size={20} />
          <div>
            <span className="label">Form Type</span>
            <span className="value">{workOrder.form_type?.toUpperCase() || '-'}</span>
          </div>
        </div>
        <div className="sipotm-info-card">
          <Building size={20} />
          <div>
            <span className="label">AMC</span>
            <span className="value">{workOrder.amc || '-'}</span>
          </div>
        </div>
        <div className="sipotm-info-card">
          <Hash size={20} />
          <div>
            <span className="label">UID</span>
            <span className="value">{workOrder.uid || '-'}</span>
          </div>
        </div>
        <div className="sipotm-info-card">
          <Clock size={20} />
          <div>
            <span className="label">Processing Time</span>
            <span className="value">{workOrder.processing_time ? `${workOrder.processing_time.toFixed(2)}s` : '-'}</span>
          </div>
        </div>
        <div className="sipotm-info-card">
          <Cpu size={20} />
          <div>
            <span className="label">Tokens</span>
            <span className="value">{workOrder.tokens?.toLocaleString() || '-'}</span>
          </div>
        </div>
        <div className="sipotm-info-card">
          <DollarSign size={20} />
          <div>
            <span className="label">Cost</span>
            <span className="value">{workOrder.cost ? `$${workOrder.cost.toFixed(6)}` : '-'}</span>
          </div>
        </div>
        <div className="sipotm-info-card">
          <Calendar size={20} />
          <div>
            <span className="label">Created</span>
            <span className="value">{formatDate(workOrder.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="sipotm-action-bar">
        <button 
          className={`sipotm-action-btn toggle ${showPdfViewer ? 'active' : ''}`}
          onClick={() => setShowPdfViewer(!showPdfViewer)}
        >
          {showPdfViewer ? <EyeOff size={16} /> : <Eye size={16} />}
          <span>{showPdfViewer ? "Hide Doc" : "Show Doc"}</span>
        </button>
        
        <div className="sipotm-action-divider"></div>
        
        <button className="sipotm-action-btn primary" onClick={handleDownloadJson}>
          <FileJson size={16} />
          <span>JSON</span>
        </button>
        <button className="sipotm-action-btn" onClick={handleDownload}>
          <Download size={16} />
          <span>Original</span>
        </button>
        
        <div className="sipotm-action-divider"></div>
        
        <button className="sipotm-action-btn refresh" onClick={fetchWorkOrder} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Split View Container */}
      <div className={`sipotm-split-container ${showPdfViewer ? 'with-pdf' : 'no-pdf'} ${pdfFullscreen ? 'pdf-fullscreen' : ''}`}>
        {/* Document Viewer Panel */}
        {showPdfViewer && (
          <div className="sipotm-pdf-panel">
            <div className="sipotm-pdf-header">
              <h3>
                {isPdf ? <FileText size={18} /> : <Image size={18} />}
                Document Preview
              </h3>
              <div className="sipotm-pdf-controls">
                <span className="sipotm-file-type-badge">
                  {workOrder.filename?.split('.').pop()?.toUpperCase() || 'FILE'}
                </span>
                {!isPdf && (
                  <>
                    <button 
                      className="sipotm-pdf-control-btn"
                      onClick={() => setZoomLevel(z => Math.max(25, z - 25))}
                      title="Zoom Out"
                    >
                      <ZoomOut size={16} />
                    </button>
                    <span className="sipotm-zoom-level">{zoomLevel}%</span>
                    <button 
                      className="sipotm-pdf-control-btn"
                      onClick={() => setZoomLevel(z => Math.min(200, z + 25))}
                      title="Zoom In"
                    >
                      <ZoomIn size={16} />
                    </button>
                    <button 
                      className="sipotm-pdf-control-btn sipotm-fit-btn"
                      onClick={() => setZoomLevel(50)}
                      title="Fit to View"
                    >
                      Fit
                    </button>
                  </>
                )}
                <button 
                  className="sipotm-pdf-control-btn"
                  onClick={() => setPdfFullscreen(!pdfFullscreen)}
                  title={pdfFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  {pdfFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button 
                  className="sipotm-pdf-control-btn"
                  onClick={handleDownload}
                  title="Download"
                >
                  <Download size={16} />
                </button>
              </div>
            </div>
            <div className="sipotm-pdf-viewer">
              {workOrderId && !fileLoadError ? (
                isImageFile(workOrder.filename) ? (
                  <div className="sipotm-image-viewer-scroll">
                    {workOrder.total_pages > 1 && (
                      <div className="sipotm-page-count-badge">
                        {workOrder.total_pages} pages
                      </div>
                    )}
                    <img
                      src={previewUrl}
                      alt={workOrder.filename || 'Document'}
                      className="sipotm-document-image-scroll"
                      onError={() => setFileLoadError(true)}
                    />
                  </div>
                ) : (
                  <iframe
                    src={previewUrl}
                    title="Document Viewer"
                    className="sipotm-pdf-iframe"
                    onError={() => setFileLoadError(true)}
                  />
                )
              ) : (
                <div className="sipotm-pdf-unavailable">
                  <File size={48} />
                  <p>{fileLoadError ? "Unable to load document" : "Document not available"}</p>
                  <button className="sipotm-download-link" onClick={handleDownload}>
                    <Download size={16} /> Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Panel */}
        <div className="sipotm-data-panel">
          {/* Tabs */}
          <div className="sipotm-tabs">
            <button
              className={`sipotm-tab ${activeTab === "sip" ? "active" : ""}`}
              onClick={() => setActiveTab("sip")}
            >
              SIP Data
              <span className="sipotm-tab-count">{getFieldCount(workOrder.sip_data)}</span>
            </button>
            <button
              className={`sipotm-tab ${activeTab === "otm" ? "active" : ""}`}
              onClick={() => setActiveTab("otm")}
            >
              OTM Data
              <span className="sipotm-tab-count">{getFieldCount(workOrder.otm_data)}</span>
            </button>
            <button
              className={`sipotm-tab ${activeTab === "coords" ? "active" : ""}`}
              onClick={() => setActiveTab("coords")}
            >
              Coordinates
            </button>
            <button
              className={`sipotm-tab ${activeTab === "full" ? "active" : ""}`}
              onClick={() => setActiveTab("full")}
            >
              <FileJson size={16} /> Full JSON
            </button>
            <button
              className={`sipotm-tab ${activeTab === "metadata" ? "active" : ""}`}
              onClick={() => setActiveTab("metadata")}
            >
              Metadata
            </button>
          </div>

          {/* Tab Content */}
          <div className="sipotm-tab-content">
            {/* SIP Data Table View */}
            {activeTab === "sip" && (
              <div className="sipotm-extracted-data">
                {workOrder.sip_data && Object.keys(workOrder.sip_data).length > 0 ? (
                  <div className="sipotm-fields-table">
                    <div className="sipotm-table-header-actions">
                      <button 
                        className="sipotm-copy-all-btn"
                        onClick={() => copyToClipboard(workOrder.sip_data, "sip_all")}
                      >
                        <Copy size={14} />
                        {copiedField === "sip_all" ? "Copied!" : "Copy All"}
                      </button>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Value</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(workOrder.sip_data).map(([key, value]) => (
                          <tr key={key}>
                            <td className="field-name">{key.replace(/_/g, ' ')}</td>
                            <td className="field-value">
                              {typeof value === "object"
                                ? JSON.stringify(value, null, 2)
                                : String(value ?? "-")}
                            </td>
                            <td className="field-actions">
                              <button
                                className="sipotm-copy-btn"
                                onClick={() => copyToClipboard(value, `sip.${key}`)}
                              >
                                <Copy size={14} />
                                {copiedField === `sip.${key}` && (
                                  <span className="copied-tooltip">Copied!</span>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="sipotm-no-data">
                    <AlertCircle size={48} />
                    <p>No SIP data available</p>
                  </div>
                )}
              </div>
            )}

            {/* OTM Data Table View */}
            {activeTab === "otm" && (
              <div className="sipotm-extracted-data">
                {workOrder.otm_data && Object.keys(workOrder.otm_data).length > 0 ? (
                  <div className="sipotm-fields-table">
                    <div className="sipotm-table-header-actions">
                      <button 
                        className="sipotm-copy-all-btn"
                        onClick={() => copyToClipboard(workOrder.otm_data, "otm_all")}
                      >
                        <Copy size={14} />
                        {copiedField === "otm_all" ? "Copied!" : "Copy All"}
                      </button>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Value</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(workOrder.otm_data).map(([key, value]) => (
                          <tr key={key}>
                            <td className="field-name">{key.replace(/_/g, ' ')}</td>
                            <td className="field-value">
                              {typeof value === "object"
                                ? JSON.stringify(value, null, 2)
                                : String(value ?? "-")}
                            </td>
                            <td className="field-actions">
                              <button
                                className="sipotm-copy-btn"
                                onClick={() => copyToClipboard(value, `otm.${key}`)}
                              >
                                <Copy size={14} />
                                {copiedField === `otm.${key}` && (
                                  <span className="copied-tooltip">Copied!</span>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="sipotm-no-data">
                    <AlertCircle size={48} />
                    <p>No OTM data available</p>
                  </div>
                )}
              </div>
            )}

            {/* Coordinates View */}
            {activeTab === "coords" && (
              <div className="sipotm-raw-json">
                <div className="sipotm-raw-json-header">
                  <button onClick={() => copyToClipboard(workOrder.field_coordinates, "coords")}>
                    <Copy size={16} /> Copy
                    {copiedField === "coords" && <span className="copied-tooltip">Copied!</span>}
                  </button>
                </div>
                <pre className="sipotm-json-viewer">
                  {workOrder.field_coordinates ? (
                    <JsonRenderer data={workOrder.field_coordinates} />
                  ) : (
                    "No coordinates data available"
                  )}
                </pre>
              </div>
            )}

            {/* Full JSON View */}
            {activeTab === "full" && (
              <div className="sipotm-raw-json">
                <div className="sipotm-raw-json-header">
                  <button onClick={handleDownloadJson}>
                    <Download size={16} /> Download
                  </button>
                  <button onClick={() => copyToClipboard(workOrder, "full")}>
                    <Copy size={16} /> Copy
                    {copiedField === "full" && <span className="copied-tooltip">Copied!</span>}
                  </button>
                </div>
                <pre className="sipotm-json-viewer">
                  <JsonRenderer data={workOrder} />
                </pre>
              </div>
            )}

            {/* Metadata View */}
            {activeTab === "metadata" && (
              <div className="sipotm-metadata">
                <table className="sipotm-metadata-table">
                  <tbody>
                    <tr>
                      <td>Work Order ID</td>
                      <td>{workOrder._id}</td>
                    </tr>
                    <tr>
                      <td>Filename</td>
                      <td>{workOrder.filename || "-"}</td>
                    </tr>
                    <tr>
                      <td>UID</td>
                      <td>{workOrder.uid || "-"}</td>
                    </tr>
                    <tr>
                      <td>Status</td>
                      <td>
                        <span className={`sipotm-status-badge ${getStatusClass(workOrder.status)}`}>
                          {workOrder.status}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>Total Pages</td>
                      <td>{workOrder.total_pages || "-"}</td>
                    </tr>
                    <tr>
                      <td>Processing Time</td>
                      <td>{workOrder.processing_time ? `${parseFloat(workOrder.processing_time).toFixed(3)}s` : "-"}</td>
                    </tr>
                    <tr>
                      <td>Model</td>
                      <td>{workOrder.model || "-"}</td>
                    </tr>
                    <tr>
                      <td>Created At</td>
                      <td>{formatDate(workOrder.created_at)}</td>
                    </tr>
                    <tr>
                      <td>Updated At</td>
                      <td>{formatDate(workOrder.updated_at)}</td>
                    </tr>
                    {workOrder.api_log && (
                      <>
                        <tr className="sipotm-metadata-section">
                          <td colSpan={2}>API Request Details</td>
                        </tr>
                        <tr>
                          <td>Method</td>
                          <td>{workOrder.api_log.method || "-"}</td>
                        </tr>
                        <tr>
                          <td>Endpoint</td>
                          <td>{workOrder.api_log.endpoint || "-"}</td>
                        </tr>
                        <tr>
                          <td>IP Address</td>
                          <td>{workOrder.api_log.ip_address || "-"}</td>
                        </tr>
                        <tr>
                          <td>API Status</td>
                          <td>
                            {workOrder.api_log.status || "-"}
                            {workOrder.api_log.status_code ? ` (${workOrder.api_log.status_code})` : ""}
                          </td>
                        </tr>
                        <tr>
                          <td>Request Payload</td>
                          <td>
                            <pre className="sipotm-metadata-payload">
                              {workOrder.api_log.request_params &&
                              Object.keys(workOrder.api_log.request_params).length > 0
                                ? JSON.stringify(workOrder.api_log.request_params, null, 2)
                                : "-"}
                            </pre>
                          </td>
                        </tr>
                        <tr>
                          <td>Response Time</td>
                          <td>
                            {workOrder.api_log.processing_time_seconds != null
                              ? `${parseFloat(workOrder.api_log.processing_time_seconds).toFixed(3)}s`
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Created By</td>
                          <td>{workOrder.api_log.created_by || "-"}</td>
                        </tr>
                        <tr>
                          <td>Timestamp</td>
                          <td>{formatDate(workOrder.api_log.timestamp)}</td>
                        </tr>
                      </>
                    )}
                    {workOrder.error && (
                      <tr className="sipotm-error-row">
                        <td>Error</td>
                        <td>{workOrder.error}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SIPOTMWorkOrderDetail;
